import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Calendar, Star, Trophy, Bell, Loader2, Gift, Edit, Save, X, ListOrdered, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';
import { useClientAuth } from '@/context/ClientAuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ProfilePage = () => {
  const { client, getAuthHeaders, updateProfile, refreshClientData } = useClientAuth();
  const [bookings, setBookings] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [waitlistEntries, setWaitlistEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [updating, setUpdating] = useState(false);
  
  const [editData, setEditData] = useState({
    first_name: '',
    last_name: '',
    phone: '',
    email: ''
  });

  useEffect(() => {
    if (client) {
      loadProfileData();
      setEditData({
        first_name: client.first_name,
        last_name: client.last_name,
        phone: client.phone,
        email: client.email
      });
    }
  }, [client]);

  const loadProfileData = async () => {
    try {
      const [bookingsRes, notifRes, waitlistRes] = await Promise.all([
        axios.get(`${API}/bookings/my-bookings`, { headers: getAuthHeaders() }),
        axios.get(`${API}/notifications/my-notifications`, { headers: getAuthHeaders() }),
        axios.get(`${API}/client/my-waitlist`, { headers: getAuthHeaders() }).catch(() => ({ data: [] })),
      ]);
      setBookings(bookingsRes.data);
      setNotifications(notifRes.data);
      setWaitlistEntries(waitlistRes.data);
    } catch (error) {
      toast.error('Errore nel caricamento del profilo');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveWaitlist = async (entryId) => {
    try {
      await axios.delete(`${API}/client/waitlist/${entryId}`, { headers: getAuthHeaders() });
      toast.success('Rimosso dalla lista d\'attesa');
      setWaitlistEntries(prev => prev.filter(e => e.id !== entryId));
    } catch (error) {
      toast.error('Errore nella rimozione');
    }
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setUpdating(true);

    const result = await updateProfile(editData);
    
    if (result.success) {
      toast.success('Profilo aggiornato con successo!');
      setEditMode(false);
      await refreshClientData();
    } else {
      toast.error(result.error);
    }
    
    setUpdating(false);
  };

  const cancelEdit = () => {
    setEditData({
      first_name: client.first_name,
      last_name: client.last_name,
      phone: client.phone,
      email: client.email
    });
    setEditMode(false);
  };

  if (!client || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-12 w-12 animate-spin text-accent" />
      </div>
    );
  }

  const loyaltyProgress = (client.loyalty_points / 10) * 100;
  const upcomingBookings = bookings.filter(b => 
    b.status !== 'cancelled' && new Date(`${b.date}T${b.time}`) > new Date()
  );
  const pastBookings = bookings.filter(b => 
    b.status === 'confirmed' && new Date(`${b.date}T${b.time}`) <= new Date()
  );

  return (
    <div className="min-h-screen py-20 container mx-auto px-4">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-12">
          <h1 className="text-4xl sm:text-5xl font-bold mb-4">Il Tuo Profilo</h1>
          <p className="text-muted-foreground text-lg">Gestisci le tue informazioni e prenotazioni</p>
        </div>

        <div className="space-y-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card className="card-elegant">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <Star className="h-5 w-5 text-accent" />
                  Informazioni Personali
                </CardTitle>
                {!editMode && (
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => setEditMode(true)}
                  >
                    <Edit className="h-4 w-4" />
                  </Button>
                )}
              </CardHeader>
              <CardContent>
                {editMode ? (
                  <form onSubmit={handleUpdateProfile} className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="first_name">Nome</Label>
                        <Input
                          id="first_name"
                          value={editData.first_name}
                          onChange={(e) => setEditData({ ...editData, first_name: e.target.value })}
                          required
                        />
                      </div>
                      <div>
                        <Label htmlFor="last_name">Cognome</Label>
                        <Input
                          id="last_name"
                          value={editData.last_name}
                          onChange={(e) => setEditData({ ...editData, last_name: e.target.value })}
                          required
                        />
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="email">Email</Label>
                      <Input
                        id="email"
                        type="email"
                        value={editData.email}
                        onChange={(e) => setEditData({ ...editData, email: e.target.value })}
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="phone">Telefono</Label>
                      <Input
                        id="phone"
                        value={editData.phone}
                        onChange={(e) => setEditData({ ...editData, phone: e.target.value })}
                        required
                      />
                    </div>
                    <div className="flex gap-2 pt-2">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={cancelEdit}
                        className="flex-1"
                      >
                        <X className="h-4 w-4 mr-2" />
                        Annulla
                      </Button>
                      <Button
                        type="submit"
                        disabled={updating}
                        className="flex-1 bg-accent text-accent-foreground hover:bg-accent/90"
                      >
                        {updating ? (
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        ) : (
                          <Save className="h-4 w-4 mr-2" />
                        )}
                        Salva
                      </Button>
                    </div>
                  </form>
                ) : (
                  <div className="space-y-4">
                    <div>
                      <p className="text-sm text-muted-foreground">Nome Completo</p>
                      <p className="text-lg font-semibold">{client.first_name} {client.last_name}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Email</p>
                      <p className="text-lg font-semibold">{client.email}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Telefono</p>
                      <p className="text-lg font-semibold">{client.phone}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Prenotazioni Totali</p>
                      <p className="text-lg font-semibold">{client.total_bookings}</p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card className="card-elegant bg-gradient-to-br from-accent/10 to-accent/5 border-accent/20">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Trophy className="h-5 w-5 text-accent" />
                  Programma Fedeltà
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm text-muted-foreground">Punti</span>
                    <span className="text-2xl font-bold text-accent">{client.loyalty_points} / 10</span>
                  </div>
                  <Progress value={loyaltyProgress} className="h-3" />
                </div>
                {client.loyalty_points >= 10 ? (
                  <div className="bg-accent/20 p-4 rounded-lg text-center">
                    <Gift className="h-8 w-8 text-accent mx-auto mb-2" />
                    <p className="font-semibold text-accent">Complimenti!</p>
                    <p className="text-sm text-muted-foreground">Hai diritto a un trattamento gratuito!</p>
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground text-center">
                    Ancora {10 - client.loyalty_points} tagli per il tuo premio!
                  </p>
                )}
              </CardContent>
            </Card>
          </div>

          {upcomingBookings.length > 0 && (
            <Card className="card-elegant">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Calendar className="h-5 w-5 text-accent" />
                  Prossimi Appuntamenti
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {upcomingBookings.map((booking) => (
                    <div key={booking.id} className="flex items-center justify-between p-4 bg-secondary rounded-lg">
                      <div>
                        <p className="font-semibold">{booking.service_name}</p>
                        <p className="text-sm text-muted-foreground">
                          {format(new Date(booking.date), 'dd MMMM yyyy', { locale: it })} alle {booking.time}
                        </p>
                        {booking.notes && (
                          <p className="text-sm text-muted-foreground mt-1">Note: {booking.notes}</p>
                        )}
                      </div>
                      <div className="text-right">
                        <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${
                          booking.status === 'confirmed' 
                            ? 'bg-accent/20 text-accent' 
                            : 'bg-yellow-500/20 text-yellow-500'
                        }`}>
                          {booking.status === 'confirmed' ? 'Confermato' : 'In Attesa'}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {waitlistEntries.length > 0 && (
            <Card className="card-elegant border-accent/20">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <ListOrdered className="h-5 w-5 text-accent" />
                  Lista d'Attesa ({waitlistEntries.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {waitlistEntries.map((entry) => (
                    <div key={entry.id} className="flex items-center justify-between p-3 bg-secondary rounded-lg" data-testid={`waitlist-entry-${entry.id}`}>
                      <div>
                        <p className="font-medium text-sm">{entry.preferred_service}</p>
                        <p className="text-xs text-muted-foreground">
                          Data: {format(new Date(entry.preferred_date), 'dd MMM yyyy', { locale: it })}
                          {entry.preferred_time && <> — Orario: {entry.preferred_time}</>}
                        </p>
                        {entry.notified && (
                          <span className="text-xs text-accent font-semibold">Posto disponibile!</span>
                        )}
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-8 w-8 p-0 text-destructive hover:text-destructive hover:bg-destructive/10 border-destructive/30"
                        onClick={() => handleRemoveWaitlist(entry.id)}
                        data-testid={`remove-waitlist-${entry.id}`}
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {notifications.length > 0 && (
            <Card className="card-elegant">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Bell className="h-5 w-5 text-accent" />
                  Notifiche
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {notifications.slice(0, 5).map((notif) => (
                    <div key={notif.id} className="flex items-start gap-3 p-3 bg-secondary rounded-lg">
                      <Bell className="h-4 w-4 text-accent mt-0.5" />
                      <div className="flex-1">
                        <p className="text-sm">{notif.message}</p>
                        <p className="text-xs text-muted-foreground mt-1">
                          {format(new Date(notif.created_at), 'dd MMM yyyy HH:mm', { locale: it })}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {pastBookings.length > 0 && (
            <Card className="card-elegant">
              <CardHeader>
                <CardTitle>Storico Prenotazioni</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {pastBookings.slice(0, 10).map((booking) => (
                    <div key={booking.id} className="flex items-center justify-between p-3 bg-secondary rounded-lg">
                      <div>
                        <p className="font-medium">{booking.service_name}</p>
                        <p className="text-sm text-muted-foreground">
                          {format(new Date(booking.date), 'dd MMM yyyy', { locale: it })}
                        </p>
                      </div>
                      <Star className="h-5 w-5 text-accent" />
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;
